import deployGuide from "../../../../documentation/customer/deploy-guide.md?raw";
import leavingStarsach from "../../../../documentation/customer/leaving-starsach.md?raw";
import rolesAndPermissions from "../../../../documentation/customer/roles-and-permissions.md?raw";
import slackNotifications from "../../../../documentation/customer/slack-notifications.md?raw";
import ssoSetup from "../../../../documentation/customer/sso-setup.md?raw";
import whatWeRead from "../../../../documentation/customer/what-we-read.md?raw";
import threeKindsOfMovement from "../../../../documentation/customer/images/three-kinds-of-movement.png";

/**
 * The customer documentation: markdown files in documentation/customer/,
 * bundled at build time so the portal can never show a page the repo
 * does not hold. Internal runbooks and ADRs are not here on purpose - the
 * portal documents the product, not Gairm's operations. Release notes
 * keep their own page (a different parser, on purpose) and appear in the
 * same navigation.
 */
export interface Doc {
  slug: string;
  title: string;
  /** One line for the index and the nav filter. */
  summary: string;
  markdown: string;
  /** Render only this `## ` section of the file (its heading is dropped; the page title stands in). */
  section?: RegExp;
}

export const DOCS: readonly Doc[] = [
  { slug: "deploy-guide", title: "Deploy guide", summary: "Get the template, deploy it in your AWS account, and operate it.", markdown: deployGuide },
  { slug: "what-leaves-your-account", title: "What leaves your account", summary: "What the template sends to Gairm from your AWS account, and what it never does.", markdown: deployGuide, section: /^4\. What leaves your account/ },
  { slug: "sso-setup", title: "Sign in with your identity provider", summary: "Connect Entra ID, Google Workspace, Okta or any OIDC/SAML provider so your people sign in with their work identity.", markdown: ssoSetup },
  { slug: "what-we-read", title: "What Starsach reads", summary: "Field by field: what leaves your account for Journeys, in what form, what is read on request and never kept, and how to check every read in your own CloudTrail.", markdown: whatWeRead },
  { slug: "roles-and-permissions", title: "Roles and permissions", summary: "What a role is, the three built in, how several add up, which profiles a person may see, groups, and where approval and break glass fit.", markdown: rolesAndPermissions },
  { slug: "slack-notifications", title: "Get notifications in Slack", summary: "Connect your Slack channels and choose, environment by environment, which notifications go to each - as well as email, never instead of it.", markdown: slackNotifications },
  { slug: "leaving-starsach", title: "Leaving Starsach", summary: "In order: change the flows that call the lander, remove the starsach: hours overrides, keep the two tables the stack deletes, delete the stack - and what Gairm holds, your export, and when it is deleted.", markdown: leavingStarsach },
];

/**
 * The images a document may show (#534), by the path its markdown writes -
 * relative to documentation/customer/, so GitHub shows them too. Each is a
 * PNG rendered at 2x from the SVG beside it (`render:doc-images`); width and
 * height are its size at 1x, reserved on the page before it loads. A path
 * not listed here is not rendered.
 */
export const DOC_IMAGES: Readonly<Record<string, { url: string; width: number; height: number }>> = {
  "images/three-kinds-of-movement.png": { url: threeKindsOfMovement, width: 1200, height: 852 },
};

export function docImage(src: string): { url: string; width: number; height: number } | undefined {
  return DOC_IMAGES[src.replace(/^\.\//, "")];
}

export function docBySlug(slug: string | undefined): Doc | undefined {
  return DOCS.find((d) => d.slug === slug);
}

/** The markdown of one `## ` section, heading excluded - up to the next `## `. Empty when the heading is not found. */
export function sectionOf(markdown: string, heading: RegExp): string {
  const lines = markdown.split("\n");
  const start = lines.findIndex((l) => l.startsWith("## ") && heading.test(l.slice(3)));
  if (start < 0) return "";
  const rest = lines.slice(start + 1);
  const end = rest.findIndex((l) => l.startsWith("## "));
  return (end < 0 ? rest : rest.slice(0, end)).join("\n").trim();
}

/** Headings in a page, for "on this page": `##` and `###`, with the ids the renderer gives them. */
export function headingsOf(markdown: string): Array<{ level: 2 | 3; text: string; id: string }> {
  return markdown
    .split("\n")
    .filter((l) => /^##{1,2} /.test(l))
    .map((l) => {
      const level = l.startsWith("### ") ? 3 : 2;
      const text = l.replace(/^##{1,2} /, "").trim();
      return { level, text, id: slugify(text) };
    });
}

/** GitHub-style: lower-case, spaces to hyphens, punctuation dropped - so a link written by hand in one file reaches a heading in another. */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[`*_]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}
